import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogIn, Search } from 'lucide-react';

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { name: 'Artists', path: '/artists' },
    { name: 'Projects', path: '/projects' },
    { name: 'Companies', path: '/companies' },
    { name: 'About', path: '/about' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="sticky top-0 z-50 bg-background border-b border-border">
      <div className="editorial-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="text-base font-bold editorial-serif tracking-tight uppercase">
              Cultural Revolution
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs uppercase tracking-widest font-medium ${
                  isActive(link.path)
                    ? 'accent-red'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Search Button */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground"
              data-testid="nav-search-btn"
              aria-label="Search"
            >
              <Search size={14} />
            </button>
            
            {/* CMS Login Link */}
            <Link
              to="/cms/login"
              className="flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-muted-foreground hover:text-foreground ml-4 pl-4 border-l border-border"
              data-testid="nav-login-link"
            >
              <LogIn size={14} />
              Login
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-border">
            <div className="flex flex-col gap-4">
              {/* Mobile Search */}
              <form onSubmit={handleSearchSubmit} className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border text-xs focus:outline-none focus:border-foreground"
                    data-testid="mobile-search-input"
                  />
                </div>
              </form>
              
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-xs uppercase tracking-widest font-medium ${
                    isActive(link.path)
                      ? 'accent-red'
                      : 'text-muted-foreground'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile CMS Login Link */}
              <Link
                to="/cms/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-muted-foreground mt-4 pt-4 border-t border-border"
                data-testid="nav-login-link-mobile"
              >
                <LogIn size={14} />
                Login
              </Link>
            </div>
          </div>
        )}
        
        {/* Desktop Search Dropdown */}
        {searchOpen && (
          <div className="hidden md:block absolute top-full left-0 right-0 bg-background border-b border-border py-4">
            <div className="editorial-container">
              <form onSubmit={handleSearchSubmit} className="max-w-xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search articles, essays, updates..."
                    className="w-full pl-12 pr-12 py-3 bg-background border border-border text-sm focus:outline-none focus:border-foreground"
                    data-testid="desktop-search-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setSearchOpen(false)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Close search"
                  >
                    <X size={18} />
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;